import { Linked, useModel } from '@type-r/react';
import { CompactInput, CompactSelect, CompactTextArea, FormCheckbox, Option, PopupIODialog, SelectFile } from 'lqx-react';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { localUserInfo } from 'api';
import { InvoiceDialogState, DisputeReason, execDisputeAction } from 'api/dispute';
import { InvoiceActionType, InvoiceModel } from 'api/invoice';
import { CollectionDiff, DiffsTable, getChangedFieldsNames, getModelsJsonDiff, ModelDiff, getModelsJsonDeltaDiff } from 'common';

const DialogTitle = {
    'dispute': 'Dispute Invoice',
    'approve': 'Approve Dispute',
    'escalateInternally': 'Escalate Internally',
    'update': 'Update invoice',
    'reject': 'Send back invoice',
    'sendBack': 'Send back invoice',
};

const SuccessMessage = {
    'dispute': 'Invoice disputed',
    'approve': 'Invoice approved',
    'escalateInternally': 'Invoice escalated',
    'update': 'Invoice updated',
    'reject': 'Invoice sent back',
    'sendBack': 'Invoice sent back',
};

const ActionButtonTitle = {
    'dispute': 'Dispute',
    'approve': 'Approve',
    'escalateInternally': 'Escalate Internally',
    'update': 'Update',
    'reject': 'Send back',
    'sendBack': 'Send back',
};

const MailSubjectPrefix = {
    'dispute': 'Dispute',
    'approve': 'Approve',
    'escalateInternally': 'Changes to',
    'update': 'Changes to',
    'reject': 'Reject Changes for',
    'sendBack': 'Reject Changes for',
};

const reasonDisabledTypes = ['approve', 'reject'];

const compareJsonProperties = {
    includeAttr: [
        'parts'
    ]
};

export const InvoiceDialog = ({ $isOpen, invoice, prevInvoice, disputedInvoice, dialogType } : {
    $isOpen : Linked<boolean>,
    invoice : InvoiceModel,
    prevInvoice : InvoiceModel,
    disputedInvoice : InvoiceModel,
    dialogType : InvoiceActionType
}) => {
    const state = useModel( InvoiceDialogState );

    const changedFields = useMemo(() =>
        getChangedFieldsNames(prevInvoice.toJSON(compareJsonProperties), invoice.toJSON(compareJsonProperties))
            .map(diff => InvoiceModel.diffNames[diff] || diff)
    , []);

    useEffect(() => {
        const invPrefix = invoice.invoiceNumber.startsWith('INV') ? '' : ' INV';
        state.invoiceId = invoice.id;
        state.emailTitle = `${MailSubjectPrefix[dialogType]}${invPrefix} ${invoice.invoiceNumber}`;
        state.toEmailList = prevInvoice.mailList(dialogType);
        state.fromEmail = localUserInfo.username;
        state.onBehalfOf = 'customer';

        if (changedFields.length) {
            state.reason = DisputeReason.Changed;
            state.change = getModelsJsonDiff(prevInvoice, invoice, compareJsonProperties);
            const changes = getModelsJsonDeltaDiff(disputedInvoice, invoice, compareJsonProperties);
            state.disputeTransaction.oldValues = changes.oldValues;
            state.disputeTransaction.deltaChange = changes.newValues;
        }

        if (invoice.hasActiveDispute) {
            const activeDispute = invoice.getActiveDispute;
            if (state.reason !== DisputeReason.Changed) {
                state.reason = activeDispute.reason;
            }

            state.issueCreditNote = activeDispute.issueCreditNote;
        }
    }, []);

    const mailTo = prevInvoice.mailList(dialogType).join('; ');
    const disableReason = reasonDisabledTypes.includes(dialogType)
        || !!changedFields.length;

    return (
        <PopupIODialog
            $isOpen={ $isOpen }
            title={ DialogTitle[dialogType] }
            actionLabel={ ActionButtonTitle[dialogType] }
            success={ SuccessMessage[dialogType] }
            disabled={ !state.isValid() }
            doIO={ async () => {
                if (state.file) {
                    state.attachmentId = await invoice.doUpload(state.file, state.comment, null, null);
                }

                await execDisputeAction(invoice.id, dialogType, state);
                prevInvoice.disputeStatus = null;
                await prevInvoice.fetch();
            }}
        >
            <CompactInput disabled label="Title" value={ state.emailTitle } />

            <CompactInput disabled label="To" tooltip={ mailTo } value={ mailTo }/>

            <CompactInput disabled label="From" value={ state.fromEmail }/>

            <CompactSelect required label="Reason" $value={ state.$.reason } disabled={ disableReason }>
                { changedFields.length ?
                    <Option key={ -1 } value={ DisputeReason.Changed }> {changedFields.join(', ')} </Option>
                : null }

                { Object.keys(DisputeReason)
                    .filter(x => x !== DisputeReason.Changed)
                    .map( ( r, i ) =>
                        <Option key={ i } value={ r }>{ DisputeReason[r] }</Option>
                )}
            </CompactSelect>
            {
                dialogType == 'dispute' && localUserInfo.user_type == 'supersupplier' ?
                    <>
                        <CompactSelect label='On behalf of' $value={state.$.onBehalfOf}>
                            <Option key='customer' value='customer'>Customer</Option>
                            <Option key='supplier' value='supplier'>Supplier</Option>
                        </CompactSelect>
                    </>
                    : <></>
            }

            <hr/>
            <DiffsTable>
                <ModelDiff label="HHH" fetched={prevInvoice} current={invoice} />
                <CollectionDiff titleAttr="partNumber" fetched={prevInvoice.parts} current={invoice.parts} />
            </DiffsTable>
            <hr/>

            <FormCheckbox $checked={ state.$.issueCreditNote} /> Issue credit note

            <CompactTextArea label="Comments"
                $value={state.$.comment}
            />

            <SelectFile label="Attachment"
                accept=".csv,.edi,.xml,.txt,.json,.pdf,.xls,.doc,.docx,.xlsx,.png,.jpeg"
                $value={ state.$.file }
            />

        </PopupIODialog>
    )
}
